import { DocumentTextIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, PencilSquareIcon as DraftIcon } from '@heroicons/react/20/solid';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    Chip,
    Column,
    ConfirmDialog,
    DataTable,
    LinkButton,
    PageHeader,
    Segmented,
    notify,
} from '../../components/ui';
import { OWNER_ABOVE } from '../../constants/roles';
import { PREVIEW_LANGUAGES } from '../../constants/languages';
import { useApiError } from '../../hooks/useApiError';
import useAuth from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../services/api';
import type { AppDispatch, RootState } from '../../store';
import { setCurrentLanguage } from '../../store/slices/languageSlice';
import { useResource } from '../MenuItems/hooks/useResource';
import { useRestaurantId, useRestaurantInfo } from '../MenuItems/hooks/useRestaurant';
import { pickName } from '../MenuItems/utils';
import type { LocationRecord, MenuRecord } from './types';

const MenuList = () => {
    const { t } = useTranslation('menu');
    const apiError = useApiError();
    const dispatch = useDispatch<AppDispatch>();
    const { hasRole } = useAuth();
    const canEdit = hasRole(OWNER_ABOVE);
    const restaurantId = useRestaurantId();
    usePageTitle(t('menus.title'));

    const menus = useResource<MenuRecord[]>(restaurantId ? '/menus' : null);
    const locations = useResource<LocationRecord[]>(restaurantId ? `/restaurants/${restaurantId}/locations` : null);
    const restaurant = useRestaurantInfo(restaurantId);
    const current = useSelector((s: RootState) => s.language.current);

    const supported = restaurant.data?.supportedLanguages ?? [];
    const lang = supported.length === 0 || supported.includes(current) ? current : supported[0];

    const [toDelete, setToDelete] = useState<MenuRecord | null>(null);
    const [deleting, setDeleting] = useState(false);

    const rows = useMemo(() => (menus.data ?? []).filter((m) => m.restaurantId === restaurantId), [menus.data, restaurantId]);
    const usedAt = useMemo(() => {
        const byMenu = new Map<string, string[]>();
        for (const l of locations.data ?? []) {
            const id = l.activeMenuId ?? l.activeMenu?.id;
            if (id) byMenu.set(id, [...(byMenu.get(id) ?? []), l.name]);
        }
        return byMenu;
    }, [locations.data]);

    const nameOf = (m: MenuRecord) => pickName(m.translations, lang) || t('editor.untitled');
    const itemCount = (m: MenuRecord) => new Set((m.categories ?? []).flatMap((c) => (c.menuItems ?? []).map((i) => i.id))).size;

    const columns: Column<MenuRecord>[] = [
        { id: 'name', header: t('menus.columns.menu'), priority: 'primary', cell: nameOf, sortValue: nameOf },
        {
            id: 'categories',
            header: t('menus.columns.categories'),
            priority: 'meta',
            align: 'end',
            width: '8rem',
            cell: (m) => m.categories?.length ?? 0,
            sortValue: (m) => m.categories?.length ?? 0,
        },
        { id: 'items', header: t('menus.columns.items'), priority: 'meta', align: 'end', width: '7rem', cell: itemCount, sortValue: itemCount },
        {
            id: 'usedAt',
            header: t('menus.columns.usedAt'),
            priority: 'secondary',
            cell: (m) => {
                const names = usedAt.get(m.id) ?? [];
                if (names.length === 0) return <span className="text-muted">{locations.status === 'ready' ? t('menus.notUsed') : '—'}</span>;
                return (
                    <span className="block max-w-[18rem] truncate" title={names.join(', ')}>
                        {names.join(', ')}
                    </span>
                );
            },
        },
        {
            id: 'status',
            header: t('menus.columns.status'),
            priority: 'secondary',
            width: '8rem',
            cell: (m) => (
                <Chip variant={m.isActive ? 'success' : 'neutral'} icon={m.isActive ? CheckCircleIcon : DraftIcon}>
                    {t(m.isActive ? 'menus.active' : 'menus.draft')}
                </Chip>
            ),
        },
    ];

    const confirmDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/menus/${toDelete.id}`);
            menus.setData((list) => list?.filter((m) => m.id !== toDelete.id) ?? list);
            notify.success(t('menus.deleted', { name: nameOf(toDelete) }));
            setToDelete(null);
        } catch (e) {
            notify.error(apiError(e));
        } finally {
            setDeleting(false);
        }
    };

    const createButton = canEdit && (
        <LinkButton to="/menus/create" variant="strong">
            {t('menus.create')}
        </LinkButton>
    );

    return (
        <>
            <PageHeader title={t('menus.title')} actions={createButton}>
                {supported.length > 1 && (
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-small text-muted">{t('menus.preview')}</span>
                        <Segmented
                            aria-label={t('menus.preview')}
                            value={lang}
                            onChange={(code) => dispatch(setCurrentLanguage(code))}
                            options={supported.map((code) => ({
                                value: code,
                                label: PREVIEW_LANGUAGES.find((l) => l.code === code)?.originalLabel ?? code,
                                lang: code,
                            }))}
                        />
                    </div>
                )}
            </PageHeader>

            <DataTable
                rows={rows}
                columns={columns}
                getRowId={(m) => m.id}
                rowHref={(m) => (canEdit ? `/menus/edit/${m.id}` : `/menus/${m.id}`)}
                rowLabel={nameOf}
                rowActions={(m) =>
                    canEdit
                        ? [
                              { id: 'edit', label: t('menus.edit'), icon: PencilSquareIcon, to: `/menus/edit/${m.id}` },
                              { id: 'delete', label: t('menus.delete'), icon: TrashIcon, tone: 'danger', separated: true, onSelect: () => setToDelete(m) },
                          ]
                        : []
                }
                state={menus.status === 'error' ? 'error' : menus.status === 'ready' ? 'ready' : 'loading'}
                onRetry={menus.reload}
                errorTitle={t('menus.loadFailed')}
                caption={t('menus.title')}
                empty={{ icon: DocumentTextIcon, title: t('menus.emptyTitle'), body: t('menus.emptyBody'), action: createButton || undefined }}
            />

            <ConfirmDialog
                open={toDelete !== null}
                onClose={() => setToDelete(null)}
                onConfirm={() => void confirmDelete()}
                busy={deleting}
                title={t('menus.deleteTitle', { name: toDelete ? nameOf(toDelete) : '' })}
            >
                {t('menus.deleteBody')}
            </ConfirmDialog>
        </>
    );
};

export default MenuList;
